/**
 * ERRO NÃO É VAZIO (07/10/2026).
 *
 * Em todo deploy de backend a API fica uns minutos fora. Nesse intervalo a tela de Propostas dizia "Nenhuma
 * proposta cadastrada" e os contadores em 0: o `catch` da carga só fazia `console`, a lista ficava vazia, e a tela
 * afirmava uma coisa sobre o BANCO que ela não tinha como saber. Medido no mesmo dia: 103 catches assim em 31
 * arquivos, e nenhuma das telas principais de lista guardava o erro.
 *
 * A regra mora aqui, numa peça só (src/design: useEstadoDaCarga e EstadoDaLista), e cada tela só liga:
 *  - carregando: nem vazio nem erro;
 *  - erro: a tela diz que NÃO CONSEGUIU carregar, o que aconteceu, e oferece tentar de novo;
 *  - pronto e vazio: aí sim "nenhuma … cadastrada".
 *
 * ⛔ ERRO NÃO ZERA OS DADOS QUE JÁ ESTAVAM NA TELA. Recarga que falha e apaga a lista que estava funcionando
 *    transforma uma falha TEMPORÁRIA em perda VISÍVEL, e é pior que o defeito original: o operador via o dado e
 *    viu sumir. A lista fica, e a tela avisa que ela é da última carga que deu certo.
 */

/**
 * 'parcial' (07/10/2026): a carga é um LOTE (um pedido por sócio, por matriz) e parte dele falhou. Antes, o item que
 * falhava sumia em silêncio (`.catch(() => null)` + filtro), e a lista saía para MENOS parecendo completa. Não vira
 * 'erro' (tudo ou nada): o erro mantém a lista da última carga boa, que numa tela por mês seria a de OUTRO mês. O
 * parcial diz a verdade da carga de agora: estes chegaram, estes (`faltaram`) não.
 */
import { textos } from './textos';

export type EstadoDaCarga = 'carregando' | 'erro' | 'parcial' | 'pronto';

export interface Carga {
  estado: EstadoDaCarga;
  /** O que aconteceu, em palavras, quando `estado` é 'erro' ou 'parcial'. */
  erro: string | null;
  /** Quando a última carga deu certo (ISO), ou null se nunca deu. Parcial conta: o que chegou é de agora. */
  atualizadoEm: string | null;
  /** Quando 'parcial': o que não chegou, nomeado para quem opera ("PARANAFRIGOR", "sócio 12"). */
  faltaram?: string[];
}

/** O que mostrar no lugar da lista. `temDados` = há itens para mostrar, de agora ou da última carga boa. */
export type OQueMostrar = 'carregando' | 'erro-sem-dados' | 'vazio' | 'lista' | 'lista-com-erro' | 'lista-parcial';

export function oQueMostrar(carga: Pick<Carga, 'estado'>, temDados: boolean): OQueMostrar {
  if (carga.estado === 'erro') return temDados ? 'lista-com-erro' : 'erro-sem-dados';
  if (carga.estado === 'parcial') return temDados ? 'lista-parcial' : 'erro-sem-dados';
  if (!temDados) return carga.estado === 'carregando' ? 'carregando' : 'vazio';
  return 'lista';
}

/** O que aconteceu, em palavras de quem opera, a partir do erro do axios (ou de qualquer erro). */
export function mensagemDoErroDeCarga(e: any): string {
  const status = e?.response?.status;
  if (status) {
    const doServidor = e?.response?.data?.message;
    const texto = Array.isArray(doServidor) ? doServidor.join('; ') : doServidor;
    return textos.servidorRespondeu(status, texto ? String(texto).slice(0, 160) : undefined);
  }
  // Prazo da requisição (services/prazoDaRequisicao): o axios corta com ECONNABORTED e "timeout of N ms exceeded".
  const prazo = /timeout of (\d+)\s*ms exceeded/i.exec(String(e?.message || ''));
  if (prazo || e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') {
    const ms = prazo ? Number(prazo[1]) : Number(e?.config?.timeout) || 0;
    return ms > 0 ? textos.servidorNaoRespondeuNoPrazo(Math.round(ms / 1000)) : textos.servidorNaoRespondeu;
  }
  if (e?.request || /network error|failed to fetch/i.test(String(e?.message || ''))) {
    return textos.servidorNaoRespondeu;
  }
  return String(e?.message || textos.erroDesconhecido);
}

/** O que uma carga de lote devolve ao `rodar` do useEstadoDaCarga: quanto pediu e o que não chegou. */
export interface ResultadoDoLote {
  total: number;
  faltaram: string[];
  /** O motivo da primeira falha, em palavras (mensagemDoErroDeCarga). */
  erro: string | null;
}

/**
 * Lê um lote, um pedido por item, sem deixar item sumir em silêncio. Devolve o que chegou e o que faltou, nomeado
 * por `rotulo`. Passe `resultado` ao fim do `rodar` para a tela dizer "faltaram N".
 *
 * É o lugar do `Promise.all(itens.map((i) => ler(i).catch(() => null)))`, que a trava
 * (utils/loteSilencioso.test.ts) não deixa mais entrar.
 */
export async function lerEmLote<T, R>(
  itens: T[],
  ler: (item: T) => Promise<R>,
  rotulo: (item: T) => string,
): Promise<{ lidos: Array<{ item: T; valor: R }>; resultado: ResultadoDoLote }> {
  const respostas = await Promise.allSettled(itens.map((i) => ler(i)));
  const lidos: Array<{ item: T; valor: R }> = [];
  const faltaram: string[] = [];
  let erro: string | null = null;
  respostas.forEach((r, k) => {
    if (r.status === 'fulfilled') lidos.push({ item: itens[k], valor: r.value });
    else {
      faltaram.push(rotulo(itens[k]));
      erro = erro ?? mensagemDoErroDeCarga(r.reason);
    }
  });
  return { lidos, resultado: { total: itens.length, faltaram, erro } };
}

/**
 * A SAÍDA NOMEADA da trava do lote silencioso. Há caso em que pular o item que falha é a decisão certa; aí o código
 * diz isso e por quê: `.catch(itemSomeDeProposito('é só um enfeite da linha; sem ele a linha segue certa'))`.
 * Sem a marca, um `.catch(() => null)` num lote reprova o build; com ela, o sumiço é decisão registrada, e uma busca
 * por `itemSomeDeProposito` lista todas.
 */
export const itemSomeDeProposito = (motivo: string) => {
  if (!motivo.trim()) throw new Error('itemSomeDeProposito: diga por que o item pode sumir');
  return (_e: unknown): null => null;
};

/**
 * Uma SEÇÃO OPCIONAL de uma tela que carrega várias coisas juntas (`Promise.all` de tupla): o lugar do
 * `servico.ler().catch(() => null)`, que fazia a seção cair calada e a tela afirmar "sem dados" ou mostrar a leitura
 * velha como atual (08/10/2026). Devolve o valor OU o erro em palavras, para a tela dizer o que não carregou.
 * A trava (utils/secaoSilenciosa.test.ts) não deixa o `.catch(() => null)` de tupla voltar.
 */
export async function lerSecao<T>(promessa: Promise<T>): Promise<{ valor: T | null; erro: string | null }> {
  try {
    return { valor: await promessa, erro: null };
  } catch (e) {
    return { valor: null, erro: mensagemDoErroDeCarga(e) };
  }
}

/**
 * A SAÍDA NOMEADA da trava da seção silenciosa: a seção cai e a tela já trata a ausência, ou o fallback é de
 * propósito. `.catch(secaoCaiDeProposito('o card lista o que ficou indisponível'))`. Exige o motivo; uma busca pelo
 * nome lista todas as decisões.
 */
export const secaoCaiDeProposito = (motivo: string) => {
  if (!motivo.trim()) throw new Error('secaoCaiDeProposito: diga por que a seção pode cair');
  return (_e: unknown): null => null;
};

/**
 * A SAÍDA NOMEADA da catraca do catch solto (scripts/check-catch-solto.js, 08/10/2026). Uma falha que pode ser
 * ignorada (gravar preferência no localStorage, copiar para a área de transferência) diz isso, e por quê:
 *   `.catch(falhaIgnoradaDeProposito('preferência de coluna; sem ela a tela abre no padrão'))`
 *   `catch (e) { falhaIgnoradaDeProposito('JSON velho no localStorage; recomeça do zero')(e); }`
 * Uma API só: recebe o motivo e devolve o handler, no molde do `itemSomeDeProposito`.
 * ⛔ Comentário dentro do catch vazio NÃO é saída: o catch vazio com o comentário "o editor já avisa na tela" era o
 * defeito da #364, o silêncio com cara de decisão. O motivo é obrigatório e não pode ser genérico ("erro",
 * "ignora"): tem de dizer por que a tela segue certa sem aquilo. Uma busca pelo nome lista todas as decisões.
 */
const MOTIVO_GENERICO = /^(?:erro|error|falha|ignora(?:r|do)?|ignore|tanto faz|nada|n\/a|-|todo|x+)$/i;
export const falhaIgnoradaDeProposito = (motivo: string) => {
  const m = motivo.trim();
  if (m.length < 10 || MOTIVO_GENERICO.test(m)) {
    throw new Error(`falhaIgnoradaDeProposito: diga por que a falha pode ser ignorada ("${m}" não diz)`);
  }
  return (_e?: unknown): undefined => undefined;
};
